
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Database, Search, Filter, TrendingUp, TrendingDown, Download, Upload, Calendar, BarChart3, MapPin } from "lucide-react";
import { PriceEntry } from "../../types";

interface PriceDatabaseProps {
  onNavigate?: (moduleId: string) => void;
}

const PriceDatabase = ({ onNavigate }: PriceDatabaseProps) => {
  const [priceEntries, setPriceEntries] = useState<PriceEntry[]>([
    {
      id: "PE-001",
      itemName: "Hospital Bed (Manual)",
      description: "Standard manual hospital bed with adjustable height",
      category: "Medical Equipment",
      unit: "Unit",
      averagePrice: 125000,
      minPrice: 95000,
      maxPrice: 180000,
      currency: "NGN",
      lastUpdated: "2024-01-15",
      dataPoints: 45,
      region: "National"
    },
    {
      id: "PE-002",
      itemName: "Laptop Computer (Core i5)",
      description: "Mid-range laptop computer with Intel Core i5 processor",
      category: "IT Equipment",
      unit: "Unit",
      averagePrice: 450000,
      minPrice: 380000,
      maxPrice: 520000,
      currency: "NGN",
      lastUpdated: "2024-01-14",
      dataPoints: 32,
      region: "National"
    },
    {
      id: "PE-003",
      itemName: "Concrete (Grade 30)",
      description: "Ready-mix concrete grade 30 for construction",
      category: "Construction Materials",
      unit: "m³",
      averagePrice: 18500,
      minPrice: 16000,
      maxPrice: 22000,
      currency: "NGN",
      lastUpdated: "2024-01-13",
      dataPoints: 78,
      region: "Lagos"
    }
  ]);

  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: currency === 'NGN' ? 'NGN' : 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getPriceTrend = (entry: PriceEntry) => {
    const variance = ((entry.maxPrice - entry.minPrice) / entry.averagePrice) * 100;
    return variance > 30 ? 'high' : variance > 15 ? 'medium' : 'low';
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const filteredEntries = priceEntries.filter(entry => {
    const matchesSearch = entry.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         entry.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === "all" || entry.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const categories = Array.from(new Set(priceEntries.map(entry => entry.category)));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Price Database</h2>
          <p className="text-gray-600">Track and analyze procurement prices across categories</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline">
            <Upload className="h-4 w-4 mr-2" />
            Import Prices
          </Button>
          <Button>
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Items</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{priceEntries.length}</div>
            <p className="text-xs text-muted-foreground">Price entries</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Categories</CardTitle>
            <Filter className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{categories.length}</div>
            <p className="text-xs text-muted-foreground">Item categories</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Data Points</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {priceEntries.reduce((sum, entry) => sum + entry.dataPoints, 0)}
            </div>
            <p className="text-xs text-muted-foreground">Total data points</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last Updated</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">Today</div>
            <p className="text-xs text-muted-foreground">Latest update</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Database className="h-5 w-5 mr-2" />
            Price Intelligence
          </CardTitle>
          <CardDescription>Historical pricing data and market trends</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex space-x-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                <Input
                  placeholder="Search items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(category => (
                  <SelectItem key={category} value={category}>{category}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item Details</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Average Price</TableHead>
                <TableHead>Price Range</TableHead>
                <TableHead>Variance</TableHead>
                <TableHead>Data Points</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEntries.map((entry) => {
                const trend = getPriceTrend(entry);
                const variance = ((entry.maxPrice - entry.minPrice) / entry.averagePrice) * 100;
                
                return (
                  <TableRow key={entry.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{entry.itemName}</div>
                        <div className="text-sm text-gray-600">{entry.description}</div>
                        <div className="text-sm text-gray-500">Unit: {entry.unit}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{entry.category}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">
                        {formatCurrency(entry.averagePrice, entry.currency)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div className="text-green-600">
                          Min: {formatCurrency(entry.minPrice, entry.currency)}
                        </div>
                        <div className="text-red-600">
                          Max: {formatCurrency(entry.maxPrice, entry.currency)}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className={`flex items-center ${getTrendColor(trend)}`}>
                        {trend === 'high' ? <TrendingUp className="h-4 w-4 mr-1" /> : 
                         trend === 'medium' ? <TrendingUp className="h-4 w-4 mr-1" /> :
                         <TrendingDown className="h-4 w-4 mr-1" />}
                        <span className="text-sm">{variance.toFixed(1)}%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{entry.dataPoints}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {new Date(entry.lastUpdated).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm">
                        View Trends
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Quick Access Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => onNavigate?.('price-schedule')}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center">
              <Calendar className="h-5 w-5 mr-2" />
              Price Update
            </CardTitle>
            <CardDescription>Manage price update schedules</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">View and configure automated price updates</p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => onNavigate?.('historical-prices')}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center">
              <BarChart3 className="h-5 w-5 mr-2" />
              Historical Prices
            </CardTitle>
            <CardDescription>Quarterly price history</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">Analyze historical pricing trends</p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => onNavigate?.('regional-factors')}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center">
              <MapPin className="h-5 w-5 mr-2" />
              Regional Factors
            </CardTitle>
            <CardDescription>Geographic cost factors</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">View regional pricing adjustments</p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => onNavigate?.('price-trends')}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center">
              <TrendingUp className="h-5 w-5 mr-2" />
              Price Trends
            </CardTitle>
            <CardDescription>Trend analysis & forecasting</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">Forecast future price movements</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PriceDatabase;
